import { useState, useEffect, useRef } from 'react';
import api from '../api';
import { fileUrl } from '../fileUrl';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const ATTACHMENT_ACCEPT = '.jpg,.jpeg,.png,.pdf,.xls,.xlsx,image/jpeg,image/png,application/pdf,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
const ALLOWED_ATTACHMENT_EXTS = ['.jpg', '.jpeg', '.png', '.pdf', '.xls', '.xlsx'];
const ALLOWED_ATTACHMENT_MIMES = [
  'image/jpeg',
  'image/png',
  'application/pdf',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const isAllowedAttachment = (file) => {
  if (!file) return false;
  const name = (file.name || '').toLowerCase();
  const hasAllowedExt = ALLOWED_ATTACHMENT_EXTS.some(ext => name.endsWith(ext));
  const hasAllowedMime = ALLOWED_ATTACHMENT_MIMES.includes(file.type);
  return hasAllowedExt && (hasAllowedMime || !file.type);
};

const getClipboardAttachment = (clipboardData) => {
  const files = Array.from(clipboardData?.files || []);
  const directFile = files.find(isAllowedAttachment);
  if (directFile) return directFile;

  const items = Array.from(clipboardData?.items || []);
  for (const item of items) {
    if (item.kind !== 'file') continue;
    const file = item.getAsFile();
    if (isAllowedAttachment(file)) return file;
  }

  return null;
};

const handleAttachmentPaste = (event, onFile) => {
  const file = getClipboardAttachment(event.clipboardData);
  if (!file) return;
  event.preventDefault();
  onFile(file);
};

/* ── Stałe ─────────────────────────────────────────────────────────────── */

const STATUS_CFG = {
  PRZYGOTOWANIE: { bg: '#1c2b3a', color: '#58a6ff', border: '#1f4068' },
  WYSLANE:       { bg: '#7c3500', color: '#ff8c00', border: '#a04800' },
  GOTOWE:        { bg: '#0d3320', color: '#3fb950', border: '#1a5c38' },
  W_DRODZE:      { bg: '#0a2010', color: '#1a7f3c', border: '#0f3a1e' },
  ZAKONCZONE:    { bg: '#3d0c0c', color: '#f85149', border: '#6b1515' },
};

const PLATNOSC_CFG = {
  NIE_ZAPLACONE: { bg: '#2d1b1b', color: '#ff7875', border: '#5c2323' },
  ZALICZKA:      { bg: '#3b1f6e', color: '#c084fc', border: '#6d3fd6' },
  OPLACONE:      { bg: '#0d3320', color: '#3fb950', border: '#1a5c38' },
};

const STATUS_LABELS = {
  WYSLANE: 'ZŁOŻONE',
};

const STATUS_OPTIONS  = Object.keys(STATUS_CFG).map(v => ({ value: v }));
const PLATNOSC_OPTIONS = Object.keys(PLATNOSC_CFG).map(v => ({ value: v }));

/* ── Małe komponenty pomocnicze ─────────────────────────────────────────── */

function Badge({ value, cfg, labels }) {
  const c = cfg[value] || { bg: '#1f1f1f', color: '#8b949e', border: '#30363d' };
  return (
    <span style={{
      background: c.bg, color: c.color, border: `1px solid ${c.border}`,
      borderRadius: '4px', padding: '2px 8px', fontSize: '12px',
      fontWeight: '600', whiteSpace: 'nowrap', display: 'inline-block',
    }}>
      {(labels && labels[value]) || value || '—'}
    </span>
  );
}

function StatusDropdown({ value, cfg, labels, options, isAdmin, onChange }) {
  const [open, setOpen]             = useState(false);
  const [hov, setHov]               = useState(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onScroll = () => setOpen(false);
    document.addEventListener('mousedown', close);
    document.addEventListener('scroll', onScroll, true);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('scroll', onScroll, true);
    };
  }, [open]);

  if (!isAdmin) return <Badge value={value} cfg={cfg} labels={labels} />;

  const handleOpen = (e) => {
    e.stopPropagation();
    if (!open) {
      const rect = e.currentTarget.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 4, left: rect.left });
    }
    setOpen(o => !o);
  };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <span
        onClick={handleOpen}
        title="Zmień status"
        style={{ cursor: 'pointer', opacity: open ? 0.8 : 1, display: 'inline-block', transition: 'opacity 0.1s' }}
      >
        <Badge value={value} cfg={cfg} labels={labels} />
      </span>
      {open && (
        <div style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, zIndex: 9999, background: '#161b22', border: '1px solid #30363d', borderRadius: '6px', padding: '4px 0', minWidth: '150px', boxShadow: '0 4px 12px rgba(0,0,0,0.5)' }}>
          {options.map(opt => (
            <div
              key={opt.value}
              onClick={e => { e.stopPropagation(); onChange(opt.value); setOpen(false); }}
              onMouseEnter={() => setHov(opt.value)}
              onMouseLeave={() => setHov(null)}
              style={{ padding: '6px 12px', cursor: 'pointer', background: hov === opt.value ? '#21262d' : 'transparent', display: 'flex', alignItems: 'center' }}
            >
              <Badge value={opt.value} cfg={cfg} labels={labels} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ListSpinner({ msg = 'Ładowanie zamówień...' }) {
  return (
    <div style={s.loadingWrap}>
      <style>{`@keyframes tm-spin { to { transform: rotate(360deg); } }`}</style>
      <div style={s.spinner} />
      <p style={{ margin: '14px 0 0', color: '#8b949e', fontSize: '14px' }}>{msg}</p>
    </div>
  );
}

function InfoRow({ label, value }) {
  return (
    <div style={ds.infoRow}>
      <span style={ds.infoLabel}>{label}</span>
      <span style={ds.infoValue}>{value ?? '—'}</span>
    </div>
  );
}

function MultiFilter({ label, options, selected, onChange, getLabel = v => v }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const selectedSet = new Set(selected);
  const toggle = (value) => {
    onChange(selectedSet.has(value)
      ? selected.filter(v => v !== value)
      : [...selected, value]);
  };
  const title = selected.length === 0 ? label : `${label}: ${selected.length}`;

  return (
    <div ref={ref} style={s.multiFilter}>
      <button type="button" style={s.multiFilterBtn} onClick={() => setOpen(o => !o)}>
        {title} <span style={s.multiFilterArrow}>{open ? '▲' : '▼'}</span>
      </button>
      {open && (
        <div style={s.multiFilterMenu}>
          <button type="button" style={s.multiFilterClear} onClick={() => onChange([])}>
            Wyczyść
          </button>
          {options.map(opt => (
            <label key={opt} style={s.multiFilterOption}>
              <input
                type="checkbox"
                checked={selectedSet.has(opt)}
                onChange={() => toggle(opt)}
                style={s.checkbox}
              />
              <span>{getLabel(opt)}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
}

function fmtUSD(val) {
  const parts = (parseFloat(val) || 0).toFixed(2).split('.');
  parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '\u00a0');
  return '$' + parts.join('.');
}

/* ── Drawer ─────────────────────────────────────────────────────────────── */

function OrderDrawer({ orderId, isAdmin, user, onClose, onDelete, onClone }) {
  const [order, setOrder]           = useState(null);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');
  const [lightbox, setLightbox]     = useState(null);
  const [newNote, setNewNote]       = useState('');
  const [savingNote, setSavingNote]     = useState(false);
  const [attachFiles, setAttachFiles]   = useState([]);
  const [attachPreview, setAttachPreview] = useState('');
  const fileInputRef = useRef(null);
  const [newComment, setNewComment] = useState('');
  const [savingComment, setSavingComment] = useState(false);
  const [itemModal, setItemModal]               = useState(null); // null | { mode:'add'|'edit', item? }
  const [itemForm, setItemForm]                 = useState({ kod_modelu: '', ilosc: '', cena_usd: '', status: 'OK', uwagi: '' });
  const [savingItem, setSavingItem]             = useState(false);
  const [itemError, setItemError]               = useState('');
  const [deleteConfirmItem, setDeleteConfirmItem] = useState(null);
  const kodModeluRef = useRef(null);
  const [orderEditOpen, setOrderEditOpen] = useState(false);
  const [orderForm, setOrderForm]         = useState({});
  const [savingOrder, setSavingOrder]     = useState(false);
  const [orderError, setOrderError]       = useState('');
  const [factories, setFactories]         = useState([]);
  const [editNoteId, setEditNoteId]       = useState(null);
  const [editNoteText, setEditNoteText]   = useState('');
  const [editCommentId, setEditCommentId]     = useState(null);
  const [editCommentText, setEditCommentText] = useState('');
  const [commentFile, setCommentFile]         = useState(null);
  const [commentPreview, setCommentPreview]   = useState('');
  const commentFileRef = useRef(null);
  const [deleteConfirmOrder, setDeleteConfirmOrder] = useState(false);
  const [deletingOrder, setDeletingOrder]           = useState(false);
  const [cloning, setCloning]                       = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError('');
    api.get(`/orders/${orderId}`)
      .then(res  => { if (alive) setOrder(res.data); })
      .catch(()  => { if (alive) setError('Błąd pobierania szczegółów zamówienia'); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [orderId]);

  const refresh = async () => {
    const res = await api.get(`/orders/${orderId}`);
    setOrder(res.data);
  };

  const fmt     = (d) => d ? new Date(d).toLocaleDateString('pl-PL') : '—';
  const fmtDate = (d) => d ? new Date(d).toISOString().slice(0, 10) : '';
  const todayDate = () => new Date().toISOString().slice(0, 10);
  const getDefaultZaliczka = () => {
    const total = (order?.items || [])
      .filter(it => it.status !== 'ANULOWANE')
      .reduce((sum, it) => sum + (Number(it.wartosc) || 0), 0);
    return (total * 0.3).toFixed(2);
  };

  const handleDeleteOrder = async () => {
    setDeletingOrder(true);
    try {
      await api.delete(`/orders/${orderId}`);
      setDeleteConfirmOrder(false);
      onDelete();
    } catch (e) {
      setDeletingOrder(false);
    }
  };

  const handleClone = async () => {
    setCloning(true);
    try {
      const res = await api.post(`/orders/${orderId}/clone`);
      onClone(res.data);
    } catch (e) {
      setCloning(false);
    }
  };

  const openEditOrder = () => {
    setOrderForm({
      nazwa_kolekcji:       order.nazwa_kolekcji || '',
      fabryka_id:           order.fabryka?.id || order.fabryka_id || '',
      status:               order.status || 'PRZYGOTOWANIE',
      platnosc_status:      order.platnosc_status || 'NIE_ZAPLACONE',
      data_zlozenia:        fmtDate(order.data_zlozenia),
      data_konca_produkcji: fmtDate(order.data_konca_produkcji),
      data_dostawy:         fmtDate(order.data_dostawy),
      zaliczka_kwota:       order.zaliczka_kwota != null ? String(order.zaliczka_kwota) : (order.platnosc_status === 'ZALICZKA' || order.platnosc_status === 'OPLACONE' ? getDefaultZaliczka() : ''),
      zaliczka_data:        fmtDate(order.zaliczka_data) || (order.platnosc_status === 'ZALICZKA' || order.platnosc_status === 'OPLACONE' ? todayDate() : ''),
      oplacone_data:        fmtDate(order.oplacone_data),
    });
    if (factories.length === 0) {
      api.get('/factories').then(res => setFactories(res.data)).catch(() => {});
    }
    setOrderError('');
    setOrderEditOpen(true);
  };

  const handlePaymentStatusChange = (status) => {
    setOrderForm(prev => {
      const next = { ...prev, platnosc_status: status };
      if (status === 'ZALICZKA' || status === 'OPLACONE') {
        if (!next.zaliczka_kwota) next.zaliczka_kwota = getDefaultZaliczka();
        if (!next.zaliczka_data) next.zaliczka_data = todayDate();
      } else {
        next.zaliczka_kwota = '';
        next.zaliczka_data = '';
        next.oplacone_data = '';
      }
      return next;
    });
  };

  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    setSavingOrder(true);
    setOrderError('');
    const showZaliczka = orderForm.platnosc_status === 'ZALICZKA' || orderForm.platnosc_status === 'OPLACONE';
    const showOplacone = orderForm.platnosc_status === 'OPLACONE';
    try {
      await api.put(`/orders/${orderId}`, {
        nazwa_kolekcji:       orderForm.nazwa_kolekcji,
        fabryka_id:           orderForm.fabryka_id ? parseInt(orderForm.fabryka_id) : undefined,
        status:               orderForm.status,
        platnosc_status:      orderForm.platnosc_status,
        data_zlozenia:        orderForm.data_zlozenia        || null,
        data_konca_produkcji: orderForm.data_konca_produkcji || null,
        data_dostawy:         orderForm.data_dostawy         || null,
        zaliczka_kwota:       showZaliczka && orderForm.zaliczka_kwota ? parseFloat(orderForm.zaliczka_kwota) : null,
        zaliczka_data:        showZaliczka && orderForm.zaliczka_data  ? orderForm.zaliczka_data  : null,
        oplacone_data:        showOplacone && orderForm.oplacone_data  ? orderForm.oplacone_data  : null,
      });
      setOrderEditOpen(false);
      await refresh();
    } catch (err) {
      setOrderError(err.response?.data?.error || 'Błąd zapisu zamówienia');
    } finally {
      setSavingOrder(false);
    }
  };

  const handleAttachFile = (file) => {
    if (!file) return;
    if (!isAllowedAttachment(file)) {
      alert('Dozwolone tylko pliki JPG, PNG, PDF, XLS i XLSX');
      return;
    }
    setAttachFiles(prev => [...prev, file]);
    if (file.type.startsWith('image/') && attachFiles.length === 0) {
      const reader = new FileReader();
      reader.onload = e => setAttachPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  const handleAttachFiles = (files) => {
    if (!files || files.length === 0) return;
    const arr = Array.from(files).filter(isAllowedAttachment);
    if (arr.length === 0) {
      alert('Dozwolone tylko pliki JPG, PNG, PDF, XLS i XLSX');
      return;
    }
    setAttachFiles(prev => [...prev, ...arr]);
    const firstImg = arr.find(f => f.type.startsWith('image/'));
    if (firstImg && attachFiles.length === 0) {
      const reader = new FileReader();
      reader.onload = e => setAttachPreview(e.target.result);
      reader.readAsDataURL(firstImg);
    }
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removeAttachFile = (idx) => {
    setAttachFiles(prev => {
      const next = prev.filter((_, i) => i !== idx);
      if (next.length === 0) setAttachPreview('');
      return next;
    });
  };

  const clearAttach = () => {
    setAttachFiles([]);
    setAttachPreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const addNoteOrPhoto = async (e) => {
    e.preventDefault();
    if (!newNote.trim() && attachFiles.length === 0) return;
    setSavingNote(true);
    try {
      if (attachFiles.length > 0) {
        for (let i = 0; i < attachFiles.length; i++) {
          const fd = new FormData();
          fd.append('plik', attachFiles[i]);
          if (i === 0 && newNote.trim()) fd.append('opis', newNote.trim());
          fd.append('typ', 'ZAMOWIENIE');
          fd.append('obiekt_id', orderId);
          await api.post('/photos', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        }
      } else {
        await api.post('/notes', {
          tresc: newNote.trim(), typ: 'ZAMOWIENIE',
          obiekt_id: orderId, created_by: user.id,
        });
      }
      setNewNote('');
      clearAttach();
      await refresh();
    } catch {} // eslint-disable-line no-empty
    finally { setSavingNote(false); }
  };

  const handleCommentFile = (file) => {
    if (!file) return;
    if (!isAllowedAttachment(file)) {
      alert('Dozwolone tylko pliki JPG, PNG, PDF, XLS i XLSX');
      return;
    }
    setCommentFile(file);
    if (file.type.startsWith('image/')) {
      const reader = new FileReader();
      reader.onload = ev => setCommentPreview(ev.target.result);
      reader.readAsDataURL(file);
    } else {
      setCommentPreview('');
    }
  };

  const clearCommentFile = () => {
    setCommentFile(null);
    setCommentPreview('');
    if (commentFileRef.current) commentFileRef.current.value = '';
  };

  const addComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim() && !commentFile) return;
    setSavingComment(true);
    try {
      const fd = new FormData();
      fd.append('tresc', newComment.trim());
      fd.append('typ', 'ZAMOWIENIE');
      fd.append('obiekt_id', orderId);
      if (commentFile) fd.append('zdjecie', commentFile);
      await api.post('/comments', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setNewComment('');
      clearCommentFile();
      await refresh();
    } catch {} // eslint-disable-line no-empty
    finally { setSavingComment(false); }
  };

  const deleteNote = async (id) => {
    try { await api.delete(`/notes/${id}`); await refresh(); }
    catch {} // eslint-disable-line no-empty
  };

  const deletePhoto = async (id) => {
    try { await api.delete(`/photos/${id}`); await refresh(); }
    catch {} // eslint-disable-line no-empty
  };

  const startEditNote = (note) => { setEditNoteId(note.id); setEditNoteText(note.tresc); };

  const saveEditNote = async (id) => {
    try {
      await api.put(`/notes/${id}`, { tresc: editNoteText });
      setEditNoteId(null);
      await refresh();
    } catch {} // eslint-disable-line no-empty
  };

  const deleteComment = async (id) => {
    try { await api.delete(`/comments/${id}`); await refresh(); }
    catch {} // eslint-disable-line no-empty
  };

  const startEditComment = (c) => { setEditCommentId(c.id); setEditCommentText(c.tresc); };

  const saveEditComment = async (id) => {
    try {
      await api.put(`/comments/${id}`, { tresc: editCommentText });
      setEditCommentId(null);
      await refresh();
    } catch {} // eslint-disable-line no-empty
  };

  const openAddItem = () => {
    setItemForm({ kod_modelu: '', ilosc: '', cena_usd: '', status: 'OK', uwagi: '' });
    setItemError('');
    setItemModal({ mode: 'add' });
  };

  const openEditItem = (item) => {
    setItemForm({
      kod_modelu: item.kod_modelu || '',
      ilosc:      String(item.ilosc ?? ''),
      cena_usd:   String(item.cena_usd ?? ''),
      status:     item.status || 'OK',
      uwagi:      item.uwagi || '',
    });
    setItemError('');
    setItemModal({ mode: 'edit', item });
  };

  const closeItemModal = () => { setItemModal(null); setItemError(''); };

  const buildPayload = () => {
    const wartosc = (parseFloat(itemForm.ilosc) || 0) * (parseFloat(itemForm.cena_usd) || 0);
    return {
      order_id:   orderId,
      kod_modelu: itemForm.kod_modelu,
      ilosc:      parseInt(itemForm.ilosc),
      cena_usd:   parseFloat(itemForm.cena_usd),
      wartosc,
      status:     itemForm.status,
      uwagi:      itemForm.uwagi || null,
    };
  };

  const handleItemSubmit = async (e) => {
    e.preventDefault();
    setSavingItem(true);
    setItemError('');
    try {
      if (itemModal.mode === 'add') {
        await api.post('/items', buildPayload());
      } else {
        await api.put(`/items/${itemModal.item.id}`, buildPayload());
      }
      closeItemModal();
      await refresh();
    } catch (err) {
      setItemError(err.response?.data?.error || 'Błąd zapisu pozycji');
    } finally {
      setSavingItem(false);
    }
  };

  const handleItemSubmitAndNext = async (e) => {
    e.preventDefault();
    setSavingItem(true);
    setItemError('');
    const saved = buildPayload();
    try {
      await api.post('/items', saved);
      await refresh();
      setItemForm({ kod_modelu: saved.kod_modelu, ilosc: String(saved.ilosc), cena_usd: String(saved.cena_usd), status: 'OK', uwagi: '' });
      setTimeout(() => {
        const el = kodModeluRef.current;
        if (el) { el.focus(); el.setSelectionRange(el.value.length, el.value.length); }
      }, 0);
    } catch (err) {
      setItemError(err.response?.data?.error || 'Błąd zapisu pozycji');
    } finally {
      setSavingItem(false);
    }
  };

  const handleDeleteItem = async (id) => {
    try {
      await api.delete(`/items/${id}`);
      setDeleteConfirmItem(null);
      await refresh();
    } catch (err) {
      setItemError(err.response?.data?.error || 'Błąd usuwania pozycji');
      setDeleteConfirmItem(null);
    }
  };

  const itemWartosc = ((parseFloat(itemForm.ilosc) || 0) * (parseFloat(itemForm.cena_usd) || 0)).toFixed(2);

  const itemsTotal = order?.items?.filter(it => it.status !== 'ANULOWANE').reduce((sum, it) => sum + (Number(it.wartosc) || 0), 0) || 0;
  const itemsQtyTotal = order?.items?.filter(it => it.status !== 'ANULOWANE').reduce((sum, it) => sum + (Number(it.ilosc) || 0), 0) || 0;

  const generatePdf = () => {
    if (!order) return;

    const pl = (str) => (str || '').replace(/ą/g,'a').replace(/ć/g,'c').replace(/ę/g,'e')
      .replace(/ł/g,'l').replace(/ń/g,'n').replace(/ó/g,'o').replace(/ś/g,'s')
      .replace(/ź/g,'z').replace(/ż/g,'z').replace(/Ą/g,'A').replace(/Ć/g,'C')
      .replace(/Ę/g,'E').replace(/Ł/g,'L').replace(/Ń/g,'N').replace(/Ó/g,'O')
      .replace(/Ś/g,'S').replace(/Ź/g,'Z').replace(/Ż/g,'Z');

    const doc = new jsPDF();
    const margin = 14;
    let y = 20;

    // Tytul
    doc.setFontSize(18);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text(pl(order.nazwa_kolekcji || '-'), margin, y);
    y += 12;

    // Naglowek
    doc.setFontSize(11);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(60, 60, 60);
    const headerLines = [
      `Fabryka: ${pl(order.fabryka?.nazwa || '-')}`,
      `Data zlozenia: ${fmt(order.data_zlozenia)}`,
      `Status platnosci: ${pl(order.platnosc_status || '-')}`,
    ];
    if (order.platnosc_status === 'ZALICZKA') {
      headerLines.push(`Kwota zaliczki: ${order.zaliczka_kwota ? Number(order.zaliczka_kwota).toFixed(2) + ' USD' : '-'}`);
      headerLines.push(`Data zaliczki: ${fmt(order.zaliczka_data)}`);
    }
    headerLines.forEach(line => { doc.text(line, margin, y); y += 7; });
    y += 4;

    // Tabela pozycji
    const items = order.items || [];
    const tableData = items.map(it => [
      pl(it.kod_modelu || '-'),
      it.ilosc ?? '-',
      Number(it.cena_usd || 0).toFixed(2),
      Number(it.wartosc || 0).toFixed(2),
      pl(it.uwagi || '-'),
    ]);
    autoTable(doc, {
      startY: y,
      head: [['Kod modelu', 'Ilosc', 'Cena (USD)', 'Wartosc (USD)', 'Uwagi']],
      body: tableData,
      theme: 'striped',
      styles: { font: 'helvetica', fontSize: 10 },
      headStyles: { fillColor: [22, 27, 34], textColor: [255, 255, 255], fontStyle: 'bold' },
      margin: { left: margin, right: margin },
    });

    // Laczna wartosc
    const total = items.filter(it => it.status !== 'ANULOWANE').reduce((sum, it) => sum + (Number(it.wartosc) || 0), 0);
    const finalY = (doc.lastAutoTable ? doc.lastAutoTable.finalY + 6 : y) + 10;
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(30, 30, 30);
    doc.text(`Laczna wartosc zamowienia: ${total.toFixed(2)} USD`, margin, finalY);

    const blob = doc.output('blob');
    window.open(URL.createObjectURL(blob), '_blank');
  };

  const isMobile = window.innerWidth < 768;
  const drawerStyle = isMobile
    ? { ...ds.drawer, width: '100vw', zIndex: 1000, borderLeft: 'none', animation: 'drawer-slide-in 250ms ease' }
    : ds.drawer;

  return (
    <>
      <style>{`@keyframes drawer-slide-in { from { transform: translateX(100%); } to { transform: translateX(0); } }`}</style>
      {/* Overlay za drawerem */}
      <div style={ds.overlay} onClick={onClose} />

      {/* Panel drawera */}
      <div style={drawerStyle}>

        {/* Nagłówek */}
        <div style={ds.drawerHeader}>
          <div style={{ minWidth: 0 }}>
            <div style={ds.drawerTitle}>{order?.nazwa_kolekcji || '...'}</div>
            <div style={ds.drawerSubtitle}>🏭 {order?.fabryka?.nazwa || '—'}</div>
          </div>
          <div style={ds.drawerHeaderBtns}>
            {isAdmin && order && (
              <button style={ds.btnEdit} onClick={openEditOrder}>Edytuj</button>
            )}
            {isAdmin && order && (
              <button style={ds.btnClone} onClick={handleClone} disabled={cloning} title="Duplikuj zamówienie">
                {cloning ? '...' : '⎘ Duplikuj'}
              </button>
            )}
            {order && (
              <button style={ds.btnReport} onClick={generatePdf}>Raport</button>
            )}
            {isAdmin && order && (
              <button style={ds.btnDelete} onClick={() => setDeleteConfirmOrder(true)} title="Usuń zamówienie">🗑</button>
            )}
            <button style={ds.btnClose} onClick={onClose}>✕</button>
          </div>
        </div>

        {/* Treść drawera */}
        <div style={ds.drawerBody}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '48px' }}>
              <style>{`@keyframes ds-spin { to { transform: rotate(360deg); } }`}</style>
              <div style={ds.spinner} />
              <p style={{ color: '#8b949e', marginTop: '14px', fontSize: '14px' }}>Ładowanie...</p>
            </div>
          ) : error ? (
            <div style={ds.errorBanner}>{error}</div>
          ) : (
            <>
              {/* ── 1. Dane podstawowe ─────────────────────────────── */}
              <div style={ds.section}>
                <h4 style={ds.sectionTitle}>Dane podstawowe</h4>
                <div style={ds.infoGrid}>
                  <InfoRow label="Status"          value={<Badge value={order.status} cfg={STATUS_CFG} labels={STATUS_LABELS} />} />
                  <InfoRow label="Płatność"         value={<Badge value={order.platnosc_status} cfg={PLATNOSC_CFG} />} />
                  <InfoRow label="Data złożenia"    value={fmt(order.data_zlozenia)} />
                  <InfoRow label="Koniec produkcji" value={fmt(order.data_konca_produkcji)} />
                  <InfoRow label="Data dostawy"     value={fmt(order.data_dostawy)} />
                  {order.platnosc_status === 'ZALICZKA' && <>
                    <InfoRow label="Zaliczka" value={order.zaliczka_kwota ? `${Number(order.zaliczka_kwota).toFixed(2)} USD` : '—'} />
                    <InfoRow label="Data zaliczki" value={fmt(order.zaliczka_data)} />
                  </>}
                </div>
              </div>

              {/* ── 2. Pozycje ─────────────────────────────────────── */}
              <div style={ds.section}>
                <div style={ds.sectionRow}>
                  <h4 style={ds.sectionTitle}>Pozycje zamówienia</h4>
                  {isAdmin && <button style={ds.btnSmallBlue} onClick={openAddItem}>+ Dodaj pozycję</button>}
                </div>
                {order.items?.length > 0 ? (
                  <div style={ds.tableWrap}>
                    <table style={ds.table}>
                      <thead>
                        <tr>
                          <th style={ds.th}>Kod modelu</th>
                          <th style={ds.thR}>Ilość</th>
                          <th style={ds.thR}>Cena USD</th>
                          <th style={ds.thR}>Wartość</th>
                          <th style={ds.th}>Status</th>
                          <th style={ds.th}>Uwagi</th>
                          {isAdmin && <th style={ds.th}></th>}
                        </tr>
                      </thead>
                      <tbody>
                        {order.items.map((it, i) => (
                          <tr key={it.id} style={i % 2 === 0 ? ds.trEven : ds.trOdd}>
                            <td style={{ ...ds.td, fontWeight: '600', color: '#e6edf3' }}>{it.kod_modelu}</td>
                            <td style={ds.tdR}>{it.ilosc}</td>
                            <td style={ds.tdR}>{Number(it.cena_usd || 0).toFixed(2)}</td>
                            <td style={{ ...ds.tdR, color: '#e6edf3', fontWeight: '600' }}>{Number(it.wartosc || 0).toFixed(2)}</td>
                            <td style={ds.td}>
                              <span style={it.status === 'OK' ? ds.badgeOk : ds.badgeCancelled}>{it.status}</span>
                            </td>
                            <td style={{ ...ds.td, color: '#8b949e' }}>{it.uwagi || '—'}</td>
                            {isAdmin && (
                              <td style={ds.td}>
                                <div style={{ display: 'flex', gap: '4px' }}>
                                  <button style={ds.btnIcon} onClick={() => openEditItem(it)}>✎</button>
                                  <button style={ds.btnIconDel} onClick={() => setDeleteConfirmItem(it)}>✕</button>
                                </div>
                              </td>
                            )}
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr style={{ background: '#0d1117' }}>
                          <td style={ds.tfTd}>Suma</td>
                          <td style={{ ...ds.tfTd, textAlign: 'right', color: '#e6edf3', fontWeight: '700' }}>
                            {itemsQtyTotal}
                          </td>
                          <td style={ds.tfTd}></td>
                          <td style={{ ...ds.tfTd, textAlign: 'right', color: '#e6edf3', fontWeight: '700' }}>
                            {itemsTotal.toFixed(2)} USD
                          </td>
                          <td style={ds.tfTd} colSpan={isAdmin ? 3 : 2}></td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : <p style={ds.emptyText}>Brak pozycji</p>}
              </div>

              {/* ── 3. Notatki i załączniki ────────────────────────── */}
              <div style={ds.section}>
                <h4 style={ds.sectionTitle}>Notatki i załączniki</h4>
                {(() => {
                  const feed = [
                    ...(order.notes  || []).map(n => ({ ...n, _type: 'note'  })),
                    ...(order.photos || []).map(p => ({ ...p, _type: 'photo' })),
                  ].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
                  if (feed.length === 0) return <p style={ds.emptyText}>Brak notatek i załączników</p>;
                  return (
                    <div style={ds.notesList}>
                      {feed.map(item => !item.plik ? (
                        <div key={`note-${item.id}`} style={ds.noteItem}>
                          <div style={ds.noteMeta}>
                            <span style={ds.noteAuthor}>{item.autor?.imie || '—'}</span>
                            <span style={ds.dot}>·</span>
                            <span style={ds.metaDate}>{fmt(item.created_at)}</span>
                            {isAdmin && (
                              <div style={{ marginLeft: 'auto', display: 'flex', gap: '4px' }}>
                                <button style={ds.btnIcon} onClick={() => startEditNote(item)}>✎</button>
                                <button style={ds.btnIconDel} onClick={() => deleteNote(item.id)}>✕</button>
                              </div>
                            )}
                          </div>
                          {editNoteId === item.id ? (
                            <>
                              <textarea
                                style={{ ...ds.textarea, marginBottom: '6px' }}
                                value={editNoteText}
                                onChange={e => setEditNoteText(e.target.value)}
                                rows={3}
                                autoFocus
                              />
                              <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                                <button style={ds.btnCancel} onClick={() => setEditNoteId(null)}>Anuluj</button>
                                <button style={ds.btnSave} onClick={() => saveEditNote(item.id)}>Zapisz</button>
                              </div>
                            </>
                          ) : (
                            <p style={ds.noteText}>{item.tresc}</p>
                          )}
                        </div>
                      ) : (
                        <div key={`photo-${item.id}`} style={ds.noteItem}>
                          <div style={ds.noteMeta}>
                            <span style={ds.noteAuthor}>{item.autor?.imie || '—'}</span>
                            <span style={ds.dot}>·</span>
                            <span style={ds.metaDate}>{fmt(item.created_at)}</span>
                            <span style={ds.dot}>·</span>
                            <span style={{ ...ds.metaDate, color: '#79c0ff' }}>
                              {item.plik?.toLowerCase().endsWith('.pdf') ? 'PDF' : (item.plik?.toLowerCase().endsWith('.xls') || item.plik?.toLowerCase().endsWith('.xlsx')) ? 'Excel' : 'zdjęcie'}
                            </span>
                            {isAdmin && (
                              <div style={{ marginLeft: 'auto' }}>
                                <button style={ds.btnIconDel} onClick={() => deletePhoto(item.id)}>✕</button>
                              </div>
                            )}
                          </div>
                          {item.plik?.toLowerCase().endsWith('.pdf') ? (
                            <a href={fileUrl(item.plik)} target="_blank" rel="noopener noreferrer" style={ds.pdfLink}>
                              📄 {item.plik}
                            </a>
                          ) : (item.plik?.toLowerCase().endsWith('.xls') || item.plik?.toLowerCase().endsWith('.xlsx')) ? (
                            <a href={fileUrl(item.plik)} target="_blank" rel="noopener noreferrer" style={ds.pdfLink}>
                              📊 {item.plik}
                            </a>
                          ) : (
                            <div style={ds.attachThumb} onClick={() => setLightbox(item)}>
                              <img src={fileUrl(item.plik)} alt={item.opis || 'zdjęcie'} style={ds.photoImg} />
                            </div>
                          )}
                          {item.opis && <p style={{ ...ds.noteText, marginTop: '6px' }}>{item.opis}</p>}
                        </div>
                      ))}
                    </div>
                  );
                })()}
                {isAdmin && (
                  <form onSubmit={addNoteOrPhoto} style={ds.addForm}>
                    <textarea
                      style={ds.textarea}
                      value={newNote}
                      onChange={e => setNewNote(e.target.value)}
                      onPaste={e => handleAttachmentPaste(e, handleAttachFile)}

                      placeholder="Treść notatki (opcjonalnie)..."
                      rows={3}
                    />
                    {attachFiles.length > 0 && (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', marginBottom: '4px' }}>
                        {attachFiles.map((f, idx) => (
                          <div key={idx} style={ds.attachFilename}>
                            {f.type.startsWith('image/') ? '🖼️' : f.type === 'application/pdf' ? '📄' : '📊'} {f.name}
                            <button type="button" style={ds.btnClearAttach} onClick={() => removeAttachFile(idx)}>✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={ATTACHMENT_ACCEPT}
                        multiple
                        style={{ display: 'none' }}
                        onChange={e => handleAttachFiles(e.target.files)}
                      />
                      <button type="button" style={ds.btnAttach} onClick={() => fileInputRef.current?.click()}>
                        📎 Załącz pliki
                      </button>
                      <button type="submit" style={ds.btnPrimary} disabled={savingNote || (!newNote.trim() && attachFiles.length === 0)}>
                        {savingNote ? 'Zapisywanie...' : 'Dodaj'}
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* ── 5. Komentarze ──────────────────────────────────── */}
              <div style={ds.section}>
                <h4 style={ds.sectionTitle}>Komentarze</h4>
                {order.comments?.length > 0 ? (
                  <div style={ds.commentsList}>
                    {order.comments.map(c => (
                      <div key={c.id} style={ds.commentItem}>
                        <div style={ds.commentMeta}>
                          <div>
                            <span style={ds.noteAuthor}>{c.autor?.imie || '—'}</span>
                            <span style={ds.dot}>·</span>
                            <span style={ds.metaDate}>{fmt(c.created_at)}</span>
                          </div>
                          {(isAdmin || c.created_by === user.id) && (
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button style={ds.btnIcon} onClick={() => startEditComment(c)}>✎</button>
                              <button style={ds.btnIconDel} onClick={() => deleteComment(c.id)}>✕</button>
                            </div>
                          )}
                        </div>
                        {editCommentId === c.id ? (
                          <>
                            <textarea
                              style={{ ...ds.textarea, marginBottom: '6px' }}
                              value={editCommentText}
                              onChange={e => setEditCommentText(e.target.value)}
                              rows={3}
                              autoFocus
                            />
                            <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-end' }}>
                              <button style={ds.btnCancel} onClick={() => setEditCommentId(null)}>Anuluj</button>
                              <button style={ds.btnSave} onClick={() => saveEditComment(c.id)}>Zapisz</button>
                            </div>
                          </>
                        ) : (
                          <>
                            {c.tresc && <p style={ds.commentText}>{c.tresc}</p>}
                            {c.zdjecie && (
                              c.zdjecie.toLowerCase().endsWith('.pdf') ? (
                                <a href={fileUrl(c.zdjecie)} target="_blank" rel="noopener noreferrer" style={ds.pdfLink}>
                                  📄 {c.zdjecie}
                                </a>
                              ) : (c.zdjecie.toLowerCase().endsWith('.xls') || c.zdjecie.toLowerCase().endsWith('.xlsx')) ? (
                                <a href={fileUrl(c.zdjecie)} target="_blank" rel="noopener noreferrer" style={ds.pdfLink}>
                                  📊 {c.zdjecie}
                                </a>
                              ) : (
                                <div style={ds.attachThumb} onClick={() => setLightbox({ plik: c.zdjecie })}>
                                  <img src={fileUrl(c.zdjecie)} alt="" style={ds.photoImg} />
                                </div>
                              )
                            )}
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                ) : <p style={ds.emptyText}>Brak komentarzy</p>}
                <form onSubmit={addComment} style={ds.addForm}>
                  <textarea
                    style={ds.textarea}
                    value={newComment}
                    onChange={e => setNewComment(e.target.value)}
                    onPaste={e => handleAttachmentPaste(e, handleCommentFile)}
                    placeholder="Napisz komentarz..."
                    rows={3}
                  />
                  {commentPreview && (
                    <div style={ds.attachPreviewWrap}>
                      <img src={commentPreview} alt="podgląd" style={ds.attachPreviewImg} />
                      <button type="button" style={ds.btnClearAttach} onClick={clearCommentFile}>✕</button>
                    </div>
                  )}
                  {commentFile && !commentPreview && (
                    <div style={ds.attachFilename}>
                      📎 {commentFile.name}
                      <button type="button" style={ds.btnClearAttach} onClick={clearCommentFile}>✕</button>
                    </div>
                  )}
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <input
                      ref={commentFileRef}
                      type="file"
                      accept={ATTACHMENT_ACCEPT}
                      style={{ display: 'none' }}
                      onChange={e => handleCommentFile(e.target.files[0])}
                    />
                    <button type="button" style={ds.btnAttach} onClick={() => commentFileRef.current?.click()}>
                      📎 Załącz plik
                    </button>
                    <button type="submit" style={ds.btnPrimary} disabled={savingComment || (!newComment.trim() && !commentFile)}>
                      {savingComment ? 'Wysyłanie...' : 'Dodaj komentarz'}
                    </button>
                  </div>
                </form>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal dodawania/edycji pozycji */}
      {itemModal && (
        <div style={ds.modalOverlay} onClick={closeItemModal}>
          <div style={ds.modal} onClick={e => e.stopPropagation()}>
            <div style={ds.modalHeader}>
              <h3 style={ds.modalTitle}>{itemModal.mode === 'add' ? 'Nowa pozycja' : 'Edytuj pozycję'}</h3>
              <button style={ds.btnClose} onClick={closeItemModal}>✕</button>
            </div>
            <form onSubmit={handleItemSubmit}>
              <div style={ds.mField}>
                <label style={ds.mLabel}>Kod modelu *</label>
                <input
                  ref={kodModeluRef}
                  style={ds.mInput}
                  value={itemForm.kod_modelu}
                  onChange={e => setItemForm({ ...itemForm, kod_modelu: e.target.value })}
                  required
                  placeholder="np. TS-2024-A"
                  autoFocus
                />
              </div>
              <div style={ds.mRow}>
                <div style={ds.mField}>
                  <label style={ds.mLabel}>Ilość *</label>
                  <input
                    style={ds.mInput}
                    type="number" min="0"
                    value={itemForm.ilosc}
                    onChange={e => setItemForm({ ...itemForm, ilosc: e.target.value })}
                    required
                  />
                </div>
                <div style={ds.mField}>
                  <label style={ds.mLabel}>Cena USD *</label>
                  <input
                    style={ds.mInput}
                    type="number" min="0" step="0.01"
                    value={itemForm.cena_usd}
                    onChange={e => setItemForm({ ...itemForm, cena_usd: e.target.value })}
                    required
                  />
                </div>
                <div style={ds.mField}>
                  <label style={ds.mLabel}>Wartość USD</label>
                  <input style={{ ...ds.mInput, color: '#8b949e' }} value={itemWartosc} readOnly />
                </div>
              </div>
              <div style={ds.mField}>
                <label style={ds.mLabel}>Status</label>
                <select
                  style={ds.mInput}
                  value={itemForm.status}
                  onChange={e => setItemForm({ ...itemForm, status: e.target.value })}
                >
                  <option value="OK">OK</option>
                  <option value="ANULOWANE">ANULOWANE</option>
                </select>
              </div>
              <div style={ds.mField}>
                <label style={ds.mLabel}>Uwagi</label>
                <textarea
                  style={{ ...ds.mInput, height: '72px', resize: 'vertical' }}
                  value={itemForm.uwagi}
                  onChange={e => setItemForm({ ...itemForm, uwagi: e.target.value })}
                  placeholder="Opcjonalne uwagi..."
                />
              </div>
              {itemError && <p style={ds.mError}>{itemError}</p>}
              <div style={ds.modalFooter}>
                <button type="button" style={ds.btnCancel} onClick={closeItemModal}>Anuluj</button>
                {itemModal.mode === 'add' && (
                  <button type="button" style={ds.btnSaveNext} disabled={savingItem} onClick={handleItemSubmitAndNext}>
                    {savingItem ? 'Zapisywanie...' : 'Zapisz i dodaj kolejny'}
                  </button>
                )}
                <button type="submit" style={ds.btnSave} disabled={savingItem}>
                  {savingItem ? 'Zapisywanie...' : 'Zapisz'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Potwierdzenie usunięcia pozycji */}
      {deleteConfirmItem && (
        <div style={ds.modalOverlay} onClick={() => setDeleteConfirmItem(null)}>
          <div style={{ ...ds.modal, maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
            <div style={ds.modalHeader}>
              <h3 style={ds.modalTitle}>Usuń pozycję</h3>
              <button style={ds.btnClose} onClick={() => setDeleteConfirmItem(null)}>✕</button>
            </div>
            <p style={ds.confirmText}>
              Czy na pewno chcesz usunąć pozycję <strong style={{ color: '#e6edf3' }}>{deleteConfirmItem.kod_modelu}</strong>?
              Tej operacji nie można cofnąć.
            </p>
            <div style={ds.modalFooter}>
              <button style={ds.btnCancel} onClick={() => setDeleteConfirmItem(null)}>Anuluj</button>
              <button style={ds.btnDelete} onClick={() => handleDeleteItem(deleteConfirmItem.id)}>Usuń</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal potwierdzenia usunięcia zamówienia */}
      {deleteConfirmOrder && (
        <div style={ds.modalOverlay} onClick={() => setDeleteConfirmOrder(false)}>
          <div style={{ ...ds.modal, maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
            <div style={ds.modalHeader}>
              <h3 style={ds.modalTitle}>Usuń zamówienie</h3>
              <button style={ds.btnClose} onClick={() => setDeleteConfirmOrder(false)}>✕</button>
            </div>
            <p style={ds.confirmText}>
              Czy na pewno chcesz usunąć zamówienie <strong style={{ color: '#e6edf3' }}>{order?.nazwa_kolekcji}</strong>?
              Zostaną usunięte wszystkie pozycje, notatki i komentarze. Tej operacji nie można cofnąć.
            </p>
            <div style={ds.modalFooter}>
              <button style={ds.btnCancel} onClick={() => setDeleteConfirmOrder(false)}>Anuluj</button>
              <button style={ds.btnDelete} onClick={handleDeleteOrder} disabled={deletingOrder}>
                {deletingOrder ? 'Usuwam...' : 'Usuń'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal edycji zamówienia */}
      {orderEditOpen && (
        <div style={ds.modalOverlay} onClick={() => setOrderEditOpen(false)}>
          <div style={{ ...ds.modal, maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
            <div style={ds.modalHeader}>
              <h3 style={ds.modalTitle}>Edytuj zamówienie</h3>
              <button style={ds.btnClose} onClick={() => setOrderEditOpen(false)}>✕</button>
            </div>
            <form onSubmit={handleOrderSubmit}>
              <div style={ds.mField}>
                <label style={ds.mLabel}>Nazwa kolekcji *</label>
                <input style={ds.mInput} value={orderForm.nazwa_kolekcji} onChange={e => setOrderForm({ ...orderForm, nazwa_kolekcji: e.target.value })} required autoFocus />
              </div>
              <div style={ds.mField}>
                <label style={ds.mLabel}>Fabryka</label>
                <select style={ds.mInput} value={orderForm.fabryka_id} onChange={e => setOrderForm({ ...orderForm, fabryka_id: e.target.value })}>
                  <option value="">— brak —</option>
                  {factories.map(f => <option key={f.id} value={f.id}>{f.nazwa}</option>)}
                </select>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                <div style={ds.mField}>
                  <label style={ds.mLabel}>Status</label>
                  <select style={ds.mInput} value={orderForm.status} onChange={e => setOrderForm({ ...orderForm, status: e.target.value })}>
                    <option value="PRZYGOTOWANIE">PRZYGOTOWANIE</option>
                    <option value="WYSLANE">ZŁOŻONE</option>
                    <option value="GOTOWE">GOTOWE</option>
                    <option value="W_DRODZE">W_DRODZE</option>
                    <option value="ZAKONCZONE">ZAKONCZONE</option>
                  </select>
                </div>
                <div style={ds.mField}>
                  <label style={ds.mLabel}>Status płatności</label>
                  <select style={ds.mInput} value={orderForm.platnosc_status} onChange={e => handlePaymentStatusChange(e.target.value)}>
                    <option value="NIE_ZAPLACONE">NIE_ZAPLACONE</option>
                    <option value="ZALICZKA">ZALICZKA</option>
                    <option value="OPLACONE">OPLACONE</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 0 }}>
                <div style={ds.mField}>
                  <label style={ds.mLabel}>Data złożenia</label>
                  <input style={ds.mInput} type="date" value={orderForm.data_zlozenia} onChange={e => setOrderForm({ ...orderForm, data_zlozenia: e.target.value })} />
                </div>
                <div style={ds.mField}>
                  <label style={ds.mLabel}>Koniec produkcji</label>
                  <input style={ds.mInput} type="date" value={orderForm.data_konca_produkcji} onChange={e => setOrderForm({ ...orderForm, data_konca_produkcji: e.target.value })} />
                </div>
                <div style={ds.mField}>
                  <label style={ds.mLabel}>Data dostawy</label>
                  <input style={ds.mInput} type="date" value={orderForm.data_dostawy} onChange={e => setOrderForm({ ...orderForm, data_dostawy: e.target.value })} />
                </div>
              </div>
              {(orderForm.platnosc_status === 'ZALICZKA' || orderForm.platnosc_status === 'OPLACONE') && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 0 }}>
                  <div style={ds.mField}>
                    <label style={ds.mLabel}>Zaliczka (USD)</label>
                    <input style={ds.mInput} type="number" min="0" step="0.01" value={orderForm.zaliczka_kwota} onChange={e => setOrderForm({ ...orderForm, zaliczka_kwota: e.target.value })} />
                  </div>
                  <div style={ds.mField}>
                    <label style={ds.mLabel}>Data zaliczki</label>
                    <input style={ds.mInput} type="date" value={orderForm.zaliczka_data} onChange={e => setOrderForm({ ...orderForm, zaliczka_data: e.target.value })} />
                  </div>
                </div>
              )}
              {orderForm.platnosc_status === 'OPLACONE' && (
                <div style={ds.mField}>
                  <label style={ds.mLabel}>Data opłacenia</label>
                  <input style={ds.mInput} type="date" value={orderForm.oplacone_data} onChange={e => setOrderForm({ ...orderForm, oplacone_data: e.target.value })} />
                </div>
              )}
              {orderError && <p style={ds.mError}>{orderError}</p>}
              <div style={{ ...ds.modalFooter, marginTop: '12px' }}>
                <button type="button" style={ds.btnCancel} onClick={() => setOrderEditOpen(false)}>Anuluj</button>
                <button type="submit" style={ds.btnSave} disabled={savingOrder}>{savingOrder ? 'Zapisywanie...' : 'Zapisz'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div style={ds.lightboxOverlay} onClick={() => setLightbox(null)}>
          <div style={ds.lightboxBox} onClick={e => e.stopPropagation()}>
            <img src={fileUrl(lightbox.plik)} alt={lightbox.opis || ''} style={ds.lightboxImg} />
            <button style={ds.lightboxClose} onClick={() => setLightbox(null)}>✕</button>
          </div>
        </div>
      )}
    </>
  );
}

/* ── Główna lista zamówień ──────────────────────────────────────────────── */

export default function Orders({ onNewOrder }) {
  const [orders, setOrders]             = useState([]);
  const [loading, setLoading]           = useState(true);
  const [error, setError]               = useState('');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [view, setView] = useState(() => localStorage.getItem('ordersView') || 'table');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [filterFabryka,  setFilterFabryka]  = useState([]);
  const [filterStatus,   setFilterStatus]   = useState([]);
  const [filterPlatnosc, setFilterPlatnosc] = useState([]);
  const [searchQuery,    setSearchQuery]    = useState('');
  const [hideCompleted,  setHideCompleted]  = useState(true);
  const [copiedAll,      setCopiedAll]      = useState(false);
  const [selectMode,     setSelectMode]     = useState(false);
  const [selectedIds,    setSelectedIds]    = useState([]);
  const [copiedSelected, setCopiedSelected] = useState(false);

  const user    = JSON.parse(localStorage.getItem('user') || '{}');
  const isAdmin = user.rola === 'ADMIN';

  useEffect(() => { fetchOrders(); }, []);

  const fetchOrders = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await api.get('/orders');
      setOrders(res.data);
    } catch {
      setError('Błąd pobierania zamówień');
    } finally {
      setLoading(false);
    }
  };

  const switchView = (v) => { setView(v); localStorage.setItem('ordersView', v); };

  const formatOrderCopyBlock = (o) => {
    const items = (o.items || []).filter(it => it.status !== 'ANULOWANE');
    const totalQty = items.reduce((sum, it) => sum + (Number(it.ilosc) || 0), 0);
    return [
      `${o.nazwa_kolekcji} - ${o.fabryka?.nazwa || ''}`,
      ...items.map(it => [it.kod_modelu, it.ilosc, it.uwagi].filter(Boolean).join(' - ')),
      `Łączna ilość sztuk: ${totalQty}`,
    ].join('\n');
  };

  const getOrdersQtyTotal = (ordersToCopy) => ordersToCopy.reduce((sum, o) => {
    return sum + (o.items || [])
      .filter(it => it.status !== 'ANULOWANE')
      .reduce((itemSum, it) => itemSum + (Number(it.ilosc) || 0), 0);
  }, 0);

  const formatOrdersCopyText = (ordersToCopy) => {
    const blocks = ordersToCopy.map(formatOrderCopyBlock);
    if (ordersToCopy.length > 1) {
      blocks.push(`Suma sztuk wszystkich skopiowanych zamówień: ${getOrdersQtyTotal(ordersToCopy)}`);
    }
    return blocks.join('\n\n');
  };

  const handleCopyAll = () => {
    navigator.clipboard.writeText(formatOrdersCopyText(sortedOrders));
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  };

  const toggleSelectMode = () => {
    setSelectMode(prev => {
      if (prev) setSelectedIds([]);
      return !prev;
    });
  };

  const toggleSelected = (id) => {
    setSelectedIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleQuickStatusChange = async (orderId, field, value) => {
    const body = { [field]: value };

    if (field === 'status' && value === 'WYSLANE') {
      body.data_zlozenia = new Date().toISOString();
    }

    if (field === 'platnosc_status' && value === 'ZALICZKA') {
      const order = orders.find(o => o.id === orderId);
      const orderVal = (order?.items || []).filter(it => it.status !== 'ANULOWANE').reduce((s, it) => s + parseFloat(it.wartosc || 0), 0);
      const default30 = (orderVal * 0.3).toFixed(2);
      const input = window.prompt('Podaj kwotę zaliczki (USD):', default30);
      if (input === null) return;                          // anulowano
      const kwota = parseFloat(input.replace(',', '.'));
      if (isNaN(kwota) || kwota < 0) return;
      body.zaliczka_kwota = kwota;
      body.zaliczka_data  = new Date().toISOString();
    }

    if (field === 'platnosc_status' && value === 'NIE_ZAPLACONE') {
      const order = orders.find(o => o.id === orderId);
      if (order?.platnosc_status === 'ZALICZKA') {
        const ok = window.confirm('Czy na pewno chcesz usunąć dane o wpłaconej zaliczce?');
        if (!ok) return;
        body.zaliczka_kwota = null;
        body.zaliczka_data  = null;
      }
    }

    try {
      await api.put(`/orders/${orderId}`, body);
      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, ...body } : o));
    } catch {
      // silent — user can open drawer to retry
    }
  };
  const handleSelect  = (order) => setSelectedOrder(order);
  const closeDrawer   = ()      => { setSelectedOrder(null); fetchOrders(); };

  const fabrykaOptions = [...new Set(orders.map(o => o.fabryka?.nazwa).filter(Boolean))].sort((a, b) => a.localeCompare(b, 'pl'));
  const statusOptions = STATUS_OPTIONS.map(opt => opt.value);
  const platnoscOptions = PLATNOSC_OPTIONS.map(opt => opt.value);

  const filteredOrders = orders.filter(o => {
    if (hideCompleted  && o.status === 'ZAKONCZONE') return false;
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      const matchKolekcja = (o.nazwa_kolekcji || '').toLowerCase().includes(q);
      const matchModel    = (o.items || []).some(it => (it.kod_modelu || '').toLowerCase().includes(q));
      if (!matchKolekcja && !matchModel) return false;
    }
    if (filterFabryka.length > 0 && !filterFabryka.includes(o.fabryka?.nazwa)) return false;
    if (filterStatus.length > 0 && !filterStatus.includes(o.status)) return false;
    if (filterPlatnosc.length > 0 && !filterPlatnosc.includes(o.platnosc_status)) return false;
    return true;
  });

  const handleSort = (key) => {
    setSortConfig(prev =>
      prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' }
    );
  };

  const getWartosc   = o => (o.items || []).filter(it => it.status !== 'ANULOWANE').reduce((sum, it) => sum + parseFloat(it.wartosc || 0), 0);
  const getDoZaplaty = o => {
    const w = getWartosc(o);
    if (o.platnosc_status === 'OPLACONE') return 0;
    if (o.platnosc_status === 'ZALICZKA') return Math.max(0, w - parseFloat(o.zaliczka_kwota || 0));
    return w;
  };

  const sortedOrders = [...filteredOrders].sort((a, b) => {
    const { key, direction } = sortConfig;
    if (!key) return 0;
    const dir = direction === 'asc' ? 1 : -1;
    switch (key) {
      case 'kolekcja': return dir * (a.nazwa_kolekcji || '').localeCompare(b.nazwa_kolekcji || '', 'pl');
      case 'fabryka':  return dir * (a.fabryka?.nazwa || '').localeCompare(b.fabryka?.nazwa || '', 'pl');
      case 'status':   return dir * (a.status || '').localeCompare(b.status || '', 'pl');
      case 'platnosc': return dir * (a.platnosc_status || '').localeCompare(b.platnosc_status || '', 'pl');
      case 'dostawa': {
        if (!a.data_konca_produkcji && !b.data_konca_produkcji) return 0;
        if (!a.data_konca_produkcji) return dir;
        if (!b.data_konca_produkcji) return -dir;
        return dir * (new Date(a.data_konca_produkcji) - new Date(b.data_konca_produkcji));
      }
      case 'wartosc':   return dir * (getWartosc(a)   - getWartosc(b));
      case 'doZaplaty': return dir * (getDoZaplaty(a) - getDoZaplaty(b));
      default: return 0;
    }
  });

  const activeOrders = filteredOrders.filter(o => o.status !== 'ZAKONCZONE');

  const naleznosci = activeOrders.reduce((sum, o) => {
    const orderVal = (o.items || []).filter(it => it.status !== 'ANULOWANE').reduce((s, it) => s + parseFloat(it.wartosc || 0), 0);
    return sum + orderVal;
  }, 0);

  const wartoscWszystkich = filteredOrders.reduce((sum, o) => {
    return sum + (o.items || []).filter(it => it.status !== 'ANULOWANE').reduce((s, it) => s + parseFloat(it.wartosc || 0), 0);
  }, 0);

  const doZaplatyTotal = filteredOrders.reduce((sum, o) => sum + getDoZaplaty(o), 0);

  const selectedSet = new Set(selectedIds);
  const selectedOrders = orders.filter(o => selectedSet.has(o.id));
  const selectedValue = selectedOrders.reduce((sum, o) => sum + getWartosc(o), 0);
  const selectedToPay = selectedOrders.reduce((sum, o) => sum + getDoZaplaty(o), 0);

  const handleCopySelected = () => {
    if (selectedOrders.length === 0) return;
    navigator.clipboard.writeText(formatOrdersCopyText(selectedOrders));
    setCopiedSelected(true);
    setTimeout(() => setCopiedSelected(false), 2000);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('pl-PL');
  };

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h2 style={s.pageTitle}>Zamówienia</h2>
          <p style={s.pageSubtitle}>
            {loading ? '...' : (filterFabryka.length > 0 || filterStatus.length > 0 || filterPlatnosc.length > 0)
              ? `${sortedOrders.length} z ${orders.length} zamówień`
              : `${orders.length} ${orders.length === 1 ? 'zamówienie' : 'zamówień'}`}
          </p>
        </div>
        <div style={s.headerRight}>
          <div style={s.viewToggle}>
            <button
              style={view === 'table' ? { ...s.toggleBtn, ...s.toggleBtnActive } : s.toggleBtn}
              onClick={() => switchView('table')}
              title="Widok tabeli"
            >☰</button>
            <button
              style={view === 'cards' ? { ...s.toggleBtn, ...s.toggleBtnActive } : s.toggleBtn}
              onClick={() => switchView('cards')}
              title="Widok kart"
            >⊞</button>
          </div>
          {isAdmin && (
            <button style={s.btnPrimary} onClick={onNewOrder}>
              + Nowe zamówienie
            </button>
          )}
        </div>
      </div>

      {/* Pasek filtrów */}
      <div style={s.filterBar}>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
          <input
            type="text"
            style={s.searchInput}
            placeholder="Szukaj kolekcji / kodu modelu…"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
          />
          <MultiFilter
            label="Wszystkie fabryki"
            options={fabrykaOptions}
            selected={filterFabryka}
            onChange={setFilterFabryka}
          />
          <MultiFilter
            label="Wszystkie statusy"
            options={statusOptions}
            selected={filterStatus}
            onChange={setFilterStatus}
            getLabel={v => STATUS_LABELS[v] || v}
          />
          {false && (
          <select style={{ display: 'none' }}>
            <option value="">Wszystkie statusy</option>
            <option value="PRZYGOTOWANIE">PRZYGOTOWANIE</option>
            <option value="WYSLANE">ZŁOŻONE</option>
            <option value="GOTOWE">GOTOWE</option>
            <option value="W_DRODZE">W_DRODZE</option>
            <option value="ZAKONCZONE">ZAKONCZONE</option>
          </select>
          )}
          <MultiFilter
            label="Wszystkie płatności"
            options={platnoscOptions}
            selected={filterPlatnosc}
            onChange={setFilterPlatnosc}
          />
          {false && (
          <select style={{ display: 'none' }} value="" onChange={() => {}}>
            <option value="">Wszystkie płatności</option>
            <option value="NIE_ZAPLACONE">NIE_ZAPLACONE</option>
            <option value="ZALICZKA">ZALICZKA</option>
            <option value="OPLACONE">OPLACONE</option>
          </select>
          )}
          <label style={s.checkLabel}>
            <input
              type="checkbox"
              checked={hideCompleted}
              onChange={e => setHideCompleted(e.target.checked)}
              style={s.checkbox}
            />
            Ukryj zakończone
          </label>
        </div>
        <div style={{ position: 'relative', display: 'flex', alignItems: 'center' }}>
          <button
            onClick={toggleSelectMode}
            title="Pokaż checkboxy przy zamówieniach"
            style={{ background: selectMode ? '#1c2b3a' : '#21262d', border: `1px solid ${selectMode ? '#1f4068' : '#30363d'}`, color: selectMode ? '#58a6ff' : '#8b949e', borderRadius: '6px', padding: '5px 12px', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', marginRight: '8px' }}
          >
            {selectMode ? '✓' : '☐'} {selectMode ? 'Zakończ wybór' : 'Wybierz zamówienia'}
          </button>
          {selectMode && (
            <button
              onClick={handleCopySelected}
              disabled={selectedIds.length === 0}
              title="Kopiuj zaznaczone zamówienia"
              style={{ background: copiedSelected ? '#0d3320' : '#21262d', border: `1px solid ${copiedSelected ? '#3fb950' : '#30363d'}`, color: copiedSelected ? '#3fb950' : selectedIds.length === 0 ? '#484f58' : '#8b949e', borderRadius: '6px', padding: '5px 12px', fontSize: '13px', cursor: selectedIds.length === 0 ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap', marginRight: '8px' }}
            >
              {copiedSelected ? '✓' : '⎘'} {copiedSelected ? 'Skopiowano!' : `Kopiuj zaznaczone (${selectedIds.length})`}
            </button>
          )}
          <button
            onClick={handleCopyAll}
            title="Kopiuj wszystkie widoczne zamówienia"
            style={{ background: copiedAll ? '#0d3320' : '#21262d', border: `1px solid ${copiedAll ? '#3fb950' : '#30363d'}`, color: copiedAll ? '#3fb950' : '#8b949e', borderRadius: '6px', padding: '5px 12px', fontSize: '13px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', whiteSpace: 'nowrap' }}
          >
            {copiedAll ? '✓' : '⎘'} {copiedAll ? 'Skopiowano!' : 'Kopiuj listę'}
          </button>
        </div>
      </div>

      {error && (
        <div style={s.errorBanner}>
          {error}
          <button style={s.retryBtn} onClick={fetchOrders}>Spróbuj ponownie</button>
        </div>
      )}

      {loading ? (
        <ListSpinner />
      ) : orders.length === 0 ? (
        <div style={s.empty}>
          <div style={s.emptyIcon}>
            <svg width="64" height="64" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect x="10" y="6" width="44" height="52" rx="4" fill="#1c2b3a" stroke="#30363d" strokeWidth="2"/>
              <rect x="22" y="2" width="20" height="8" rx="3" fill="#0d1117" stroke="#30363d" strokeWidth="2"/>
              <line x1="20" y1="24" x2="44" y2="24" stroke="#30363d" strokeWidth="2" strokeLinecap="round"/>
              <line x1="20" y1="33" x2="44" y2="33" stroke="#30363d" strokeWidth="2" strokeLinecap="round"/>
              <line x1="20" y1="42" x2="34" y2="42" stroke="#30363d" strokeWidth="2" strokeLinecap="round"/>
              <circle cx="16" cy="24" r="2" fill="#1890ff"/>
              <circle cx="16" cy="33" r="2" fill="#1890ff"/>
              <circle cx="16" cy="42" r="2" fill="#1890ff"/>
            </svg>
          </div>
          <p style={s.emptyText}>Brak zamówień</p>
          {isAdmin && (
            <button style={s.btnPrimary} onClick={onNewOrder}>
              Dodaj pierwsze zamówienie
            </button>
          )}
        </div>
      ) : view === 'table' ? (
        <TableView orders={sortedOrders} onSelect={handleSelect} formatDate={formatDate} fmtUSD={fmtUSD} isAdmin={isAdmin} onStatusChange={handleQuickStatusChange} sortConfig={sortConfig} onSort={handleSort} selectMode={selectMode} selectedIds={selectedIds} onToggleSelected={toggleSelected} />
      ) : (
        <CardsView orders={sortedOrders} onSelect={handleSelect} formatDate={formatDate} fmtUSD={fmtUSD} isAdmin={isAdmin} onStatusChange={handleQuickStatusChange} selectMode={selectMode} selectedIds={selectedIds} onToggleSelected={toggleSelected} />
      )}

      {!loading && filteredOrders.length > 0 && (
        <div style={s.summaryBox}>
          <div style={s.summaryItem}>
            <span style={s.summaryLabel}>Wartość wszystkich zamówień:</span>
            <span style={s.summaryValue}>{fmtUSD(wartoscWszystkich)}</span>
          </div>
          <div style={s.summaryItem}>
            <span style={s.summaryLabel}>Wartość bieżących zamówień:</span>
            <span style={s.summaryValue}>{fmtUSD(naleznosci)}</span>
          </div>
          <div style={s.summaryItem}>
            <span style={s.summaryLabel}>Do zapłaty:</span>
            <span style={{ ...s.summaryValue, color: '#ff8c00' }}>{fmtUSD(Math.max(0, doZaplatyTotal))}</span>
          </div>
        </div>
      )}

      {selectMode && selectedOrders.length > 0 && (
        <div style={{ ...s.summaryBox, borderColor: '#1f4068', background: '#0f1b2a' }}>
          <div style={s.summaryItem}>
            <span style={s.summaryLabel}>Zaznaczone zamówienia:</span>
            <span style={s.summaryValue}>{selectedOrders.length}</span>
          </div>
          <div style={s.summaryItem}>
            <span style={s.summaryLabel}>Wartość zaznaczonych zamówień:</span>
            <span style={s.summaryValue}>{fmtUSD(selectedValue)}</span>
          </div>
          <div style={s.summaryItem}>
            <span style={s.summaryLabel}>Do zapłaty zaznaczone zamówienia:</span>
            <span style={{ ...s.summaryValue, color: '#ff8c00' }}>{fmtUSD(selectedToPay)}</span>
          </div>
        </div>
      )}

      {/* Drawer */}
      {selectedOrder && (
        <OrderDrawer
          orderId={selectedOrder.id}
          isAdmin={isAdmin}
          user={user}
          onClose={closeDrawer}
          onDelete={closeDrawer}
          onClone={(cloned) => { fetchOrders(); setSelectedOrder(cloned); }}
        />
      )}
    </div>
  );
}

/* ── Widok tabeli ───────────────────────────────────────────────────────── */

function TableView({ orders, onSelect, formatDate, fmtUSD, isAdmin, onStatusChange, sortConfig, onSort, selectMode, selectedIds, onToggleSelected }) {
  const [hovTh, setHovTh] = useState(null);

  const Th = ({ colKey, label, right }) => {
    const active = sortConfig.key === colKey;
    const base   = right ? s.thR : s.th;
    return (
      <th
        style={{ ...base, cursor: 'pointer', background: hovTh === colKey ? '#21262d' : '#0d1117', userSelect: 'none' }}
        onClick={() => onSort(colKey)}
        onMouseEnter={() => setHovTh(colKey)}
        onMouseLeave={() => setHovTh(null)}
      >
        {label}
        {active && <span style={{ color: '#1890ff', marginLeft: '4px' }}>{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>}
      </th>
    );
  };

  return (
    <div style={s.tableWrapper}>
      <style>{`@keyframes tr-expand { from { opacity: 0; transform: translateY(-4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
      <table style={s.table}>
        <thead>
          <tr>
            {selectMode && <th style={{ ...s.th, width: '32px', padding: '9px 8px' }}></th>}
            <th style={{ ...s.th, width: '32px', padding: '9px 8px' }}></th>
            <Th colKey="kolekcja" label="Kolekcja" />
            <Th colKey="fabryka"  label="Fabryka" />
            <Th colKey="status"   label="Status" />
            <Th colKey="platnosc" label="Płatność" />
            <Th colKey="dostawa"  label="Koniec produkcji" />
            <Th colKey="wartosc"   label="Wartość"    right />
            <Th colKey="doZaplaty" label="Do zapłaty" right />
            <th style={s.th}></th>
          </tr>
        </thead>
        <tbody>
          {orders.map((o, i) => (
            <TableRow key={o.id} order={o} index={i} onSelect={onSelect} formatDate={formatDate} fmtUSD={fmtUSD} isAdmin={isAdmin} onStatusChange={onStatusChange} selectMode={selectMode} selected={selectedIds.includes(o.id)} onToggleSelected={onToggleSelected} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function TableRow({ order: o, index: i, onSelect, formatDate, fmtUSD, isAdmin, onStatusChange, selectMode, selected, onToggleSelected }) {
  const [hovered, setHovered]   = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied]     = useState(false);

  const handleCopy = (e) => {
    e.stopPropagation();
    const items = (o.items || []).filter(it => it.status !== 'ANULOWANE');
    const totalQty = items.reduce((sum, it) => sum + (Number(it.ilosc) || 0), 0);
    const lines = [
      `${o.nazwa_kolekcji} - ${o.fabryka?.nazwa || ''}`,
      ...items.map(it => [it.kod_modelu, it.ilosc, it.uwagi].filter(Boolean).join(' - ')),
      `Łączna ilość sztuk: ${totalQty}`,
    ];
    navigator.clipboard.writeText(lines.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  const base    = i % 2 === 0 ? '#161b22' : '#0d1117';
  const wartosc = (o.items || []).filter(it => it.status !== 'ANULOWANE').reduce((sum, it) => sum + parseFloat(it.wartosc || 0), 0);
  const doZaplaty = o.platnosc_status === 'OPLACONE'
    ? null
    : o.platnosc_status === 'ZALICZKA'
      ? Math.max(0, wartosc - parseFloat(o.zaliczka_kwota || 0))
      : wartosc;
  return (
    <>
      <tr
        style={{ background: hovered ? '#1c2128' : base, cursor: 'pointer', transition: 'background 0.1s' }}
        onClick={() => onSelect(o)}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        {selectMode && (
          <td style={{ ...s.td, width: '32px', padding: '9px 8px 9px 12px' }} onClick={e => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={selected}
              onChange={() => onToggleSelected(o.id)}
              style={s.checkbox}
            />
          </td>
        )}
        <td style={{ ...s.td, width: '32px', padding: '9px 8px 9px 12px' }}>
          <button
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#8b949e', fontSize: '11px', padding: '2px 4px', lineHeight: 1, borderRadius: '3px' }}
            onClick={e => { e.stopPropagation(); setExpanded(v => !v); }}
            title={expanded ? 'Zwiń' : 'Rozwiń pozycje'}
          >
            {expanded ? '▼' : '▶'}
          </button>
        </td>
        <td style={{ ...s.td, fontWeight: '600', color: '#e6edf3' }}>{o.nazwa_kolekcji}</td>
        <td style={s.td}>{o.fabryka?.nazwa || <span style={s.dash}>—</span>}</td>
        <td style={s.td}><StatusDropdown value={o.status} cfg={STATUS_CFG} labels={STATUS_LABELS} options={STATUS_OPTIONS} isAdmin={isAdmin} onChange={v => onStatusChange(o.id, 'status', v)} /></td>
        <td style={s.td}><StatusDropdown value={o.platnosc_status} cfg={PLATNOSC_CFG} options={PLATNOSC_OPTIONS} isAdmin={isAdmin} onChange={v => onStatusChange(o.id, 'platnosc_status', v)} /></td>
        <td style={{ ...s.td, color: '#8b949e' }} onClick={e => e.stopPropagation()}>
          {isAdmin
            ? <input
                type="date"
                defaultValue={o.data_konca_produkcji ? o.data_konca_produkcji.slice(0, 10) : ''}
                onBlur={e => onStatusChange(o.id, 'data_konca_produkcji', e.target.value || null)}
                onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                style={{ background: 'none', border: 'none', color: '#8b949e', fontSize: '13px', cursor: 'pointer', width: '110px', outline: 'none' }}
              />
            : formatDate(o.data_konca_produkcji)
          }
        </td>
        <td style={{ ...s.td, ...s.tdR, fontWeight: '600', color: '#e6edf3' }}>{fmtUSD(wartosc)}</td>
        <td style={{ ...s.td, ...s.tdR, fontWeight: '600', color: doZaplaty === null ? '#484f58' : '#ff8c00' }}>
          {doZaplaty === null ? '—' : fmtUSD(doZaplaty)}
        </td>
        <td style={{ ...s.td, width: '40px', textAlign: 'center', position: 'relative' }}>
          <button
            onClick={handleCopy}
            title="Kopiuj do schowka"
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: copied ? '#3fb950' : '#8b949e', fontSize: '14px', padding: '2px 4px', borderRadius: '3px', lineHeight: 1 }}
          >
            {copied ? '✓' : '⎘'}
          </button>
          {copied && (
            <span style={{ position: 'absolute', right: '36px', top: '50%', transform: 'translateY(-50%)', background: '#3fb950', color: '#0d1117', fontSize: '11px', fontWeight: '600', padding: '2px 7px', borderRadius: '4px', whiteSpace: 'nowrap', pointerEvents: 'none' }}>
              Skopiowano!
            </span>
          )}
        </td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={selectMode ? 10 : 9} style={{ padding: 0, background: '#0d1117', borderBottom: '2px solid #30363d' }}>
            <div style={{ animation: 'tr-expand 0.15s ease', padding: '10px 16px 12px 44px' }}>
              {o.items?.length > 0 ? (
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #21262d' }}>
                      <th style={s.expandTh}>Kod modelu</th>
                      <th style={{ ...s.expandTh, textAlign: 'right' }}>Ilość</th>
                      <th style={{ ...s.expandTh, textAlign: 'right' }}>Cena USD</th>
                      <th style={{ ...s.expandTh, textAlign: 'right' }}>Wartość</th>
                      <th style={s.expandTh}>Status</th>
                      <th style={s.expandTh}>Uwagi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {o.items.map(it => {
                      const cancelled = it.status === 'ANULOWANE';
                      const cell = { padding: '5px 10px', fontSize: '12px', color: cancelled ? '#484f58' : '#c9d1d9', textDecoration: cancelled ? 'line-through' : 'none', borderBottom: '1px solid #161b22' };
                      return (
                        <tr key={it.id}>
                          <td style={{ ...cell, fontWeight: '500' }}>{it.kod_modelu}</td>
                          <td style={{ ...cell, textAlign: 'right' }}>{it.ilosc}</td>
                          <td style={{ ...cell, textAlign: 'right' }}>{fmtUSD(it.cena_usd)}</td>
                          <td style={{ ...cell, textAlign: 'right', fontWeight: '600' }}>{fmtUSD(it.wartosc)}</td>
                          <td style={cell}>{it.status}</td>
                          <td style={{ ...cell, color: cancelled ? '#484f58' : '#484f58' }}>{it.uwagi || <span style={{ fontStyle: 'italic' }}>—</span>}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <p style={{ margin: 0, color: '#484f58', fontSize: '13px', fontStyle: 'italic' }}>Brak pozycji</p>
              )}
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* ── Widok kart ─────────────────────────────────────────────────────────── */

function CardsView({ orders, onSelect, formatDate, fmtUSD, isAdmin, onStatusChange, selectMode, selectedIds, onToggleSelected }) {
  return (
    <div style={s.cardsGrid}>
      {orders.map(o => (
        <OrderCard key={o.id} order={o} onSelect={onSelect} formatDate={formatDate} fmtUSD={fmtUSD} isAdmin={isAdmin} onStatusChange={onStatusChange} selectMode={selectMode} selected={selectedIds.includes(o.id)} onToggleSelected={onToggleSelected} />
      ))}
    </div>
  );
}

function OrderCard({ order: o, onSelect, formatDate, fmtUSD, isAdmin, onStatusChange, selectMode, selected, onToggleSelected }) {
  const [hovered, setHovered] = useState(false);
  const wartosc = (o.items || []).filter(it => it.status !== 'ANULOWANE').reduce((sum, it) => sum + parseFloat(it.wartosc || 0), 0);
  return (
    <div
      style={{
        ...s.card,
        borderColor: hovered ? '#1890ff' : '#30363d',
        boxShadow: hovered ? '0 0 0 1px #1890ff33' : 'none',
      }}
      onClick={() => onSelect(o)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <div style={s.cardHeader}>
        {selectMode && (
          <input
            type="checkbox"
            checked={selected}
            onChange={e => { e.stopPropagation(); onToggleSelected(o.id); }}
            onClick={e => e.stopPropagation()}
            style={s.checkbox}
          />
        )}
        <span style={s.cardTitle}>{o.nazwa_kolekcji}</span>
        <StatusDropdown value={o.status} cfg={STATUS_CFG} labels={STATUS_LABELS} options={STATUS_OPTIONS} isAdmin={isAdmin} onChange={v => onStatusChange(o.id, 'status', v)} />
      </div>
      <div style={s.cardFactory}>
        <span style={{ marginRight: '6px' }}>🏭</span>
        {o.fabryka?.nazwa || <span style={s.dash}>Brak fabryki</span>}
      </div>
      <div style={s.cardFooter}>
        <StatusDropdown value={o.platnosc_status} cfg={PLATNOSC_CFG} options={PLATNOSC_OPTIONS} isAdmin={isAdmin} onChange={v => onStatusChange(o.id, 'platnosc_status', v)} />
        {isAdmin
          ? <label style={{ ...s.cardDate, cursor: 'pointer' }} onClick={e => e.stopPropagation()}>
              📅 <input
                type="date"
                defaultValue={o.data_konca_produkcji ? o.data_konca_produkcji.slice(0, 10) : ''}
                onBlur={e => onStatusChange(o.id, 'data_konca_produkcji', e.target.value || null)}
                onKeyDown={e => { if (e.key === 'Enter') e.target.blur(); }}
                style={{ background: 'none', border: 'none', color: '#8b949e', fontSize: '12px', outline: 'none' }}
              />
            </label>
          : <span style={s.cardDate}>📅 {formatDate(o.data_konca_produkcji)}</span>
        }
      </div>
      <div style={s.cardValue}>{fmtUSD(wartosc)}</div>
    </div>
  );
}

/* ── Style listy (s) ────────────────────────────────────────────────────── */

const s = {
  page: { padding: '32px', color: '#e6edf3', width: '100%', maxWidth: '100%', boxSizing: 'border-box', overflowX: 'hidden' },

  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' },
  pageTitle: { margin: '0 0 4px', fontSize: '22px', fontWeight: '700', color: '#e6edf3' },
  pageSubtitle: { margin: 0, color: '#8b949e', fontSize: '14px' },

  headerRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  viewToggle: { display: 'flex', background: '#161b22', border: '1px solid #30363d', borderRadius: '6px', overflow: 'hidden' },
  toggleBtn: { padding: '8px 14px', background: 'none', border: 'none', color: '#8b949e', fontSize: '17px', cursor: 'pointer', lineHeight: 1 },
  toggleBtnActive: { background: '#1890ff', color: 'white' },

  btnPrimary: { padding: '10px 20px', background: '#1890ff', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer', fontWeight: '500' },

  sortSelect:  { padding: '8px 12px', background: '#161b22', border: '1px solid #30363d', borderRadius: '6px', color: '#c9d1d9', fontSize: '13px', cursor: 'pointer', outline: 'none' },
  multiFilter: { position: 'relative' },
  multiFilterBtn: { padding: '8px 12px', background: '#161b22', border: '1px solid #30363d', borderRadius: '6px', color: '#c9d1d9', fontSize: '13px', cursor: 'pointer', outline: 'none', minWidth: '150px', textAlign: 'left', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px' },
  multiFilterArrow: { color: '#8b949e', fontSize: '10px' },
  multiFilterMenu: { position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 50, minWidth: '220px', maxHeight: '280px', overflowY: 'auto', background: '#161b22', border: '1px solid #30363d', borderRadius: '6px', boxShadow: '0 8px 24px rgba(0,0,0,0.45)', padding: '6px' },
  multiFilterClear: { width: '100%', padding: '6px 8px', marginBottom: '4px', background: '#21262d', border: '1px solid #30363d', borderRadius: '4px', color: '#8b949e', fontSize: '12px', cursor: 'pointer', textAlign: 'left' },
  multiFilterOption: { display: 'flex', alignItems: 'center', gap: '8px', padding: '7px 8px', color: '#c9d1d9', fontSize: '13px', cursor: 'pointer', borderRadius: '4px', whiteSpace: 'nowrap' },
  checkLabel:  { display: 'flex', alignItems: 'center', gap: '7px', fontSize: '13px', color: '#8b949e', cursor: 'pointer', whiteSpace: 'nowrap' },
  checkbox:    { accentColor: '#1890ff', width: '14px', height: '14px', cursor: 'pointer' },
  searchInput: { padding: '8px 12px', background: '#0d1117', border: '1px solid #30363d', borderRadius: '6px', color: '#c9d1d9', fontSize: '13px', outline: 'none', width: '220px', fontFamily: 'inherit' },

  errorBanner: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#2d1b1b', border: '1px solid #5c2323', color: '#ff7875', padding: '12px 16px', borderRadius: '6px', marginBottom: '16px' },
  retryBtn: { background: 'none', border: '1px solid #5c2323', color: '#ff7875', borderRadius: '4px', padding: '4px 10px', fontSize: '12px', cursor: 'pointer' },

  loadingWrap: { textAlign: 'center', padding: '60px' },
  spinner: { width: '36px', height: '36px', margin: '0 auto', border: '3px solid #30363d', borderTopColor: '#1890ff', borderRadius: '50%', animation: 'tm-spin 0.8s linear infinite' },

  empty: { textAlign: 'center', padding: '60px', color: '#8b949e' },
  emptyIcon: { marginBottom: '12px', lineHeight: 0 },
  emptyText: { fontSize: '16px', marginBottom: '20px' },

  tableWrapper: { background: '#161b22', borderRadius: '8px', overflowX: 'auto', boxShadow: '0 1px 4px rgba(0,0,0,0.4)', border: '1px solid #30363d', width: '100%', boxSizing: 'border-box' },
  table: { width: '100%', minWidth: '700px', borderCollapse: 'collapse' },
  th:  { padding: '12px 16px', background: '#0d1117', textAlign: 'left',  borderBottom: '1px solid #30363d', fontWeight: '600', fontSize: '13px', color: '#8b949e' },
  thR: { padding: '12px 16px', background: '#0d1117', textAlign: 'right', borderBottom: '1px solid #30363d', fontWeight: '600', fontSize: '13px', color: '#8b949e' },
  td:  { padding: '12px 16px', borderBottom: '1px solid #21262d', fontSize: '14px', verticalAlign: 'middle', color: '#c9d1d9' },
  tdR: { textAlign: 'right' },
  dash: { color: '#484f58' },
  expandTh: { padding: '5px 10px', textAlign: 'left', color: '#8b949e', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.04em' },

  filterBar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#161b22', borderBottom: '1px solid #30363d', padding: '10px 24px', gap: '8px', flexWrap: 'wrap', width: '100%', boxSizing: 'border-box' },
  summaryBox: { marginTop: '20px', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '32px', flexWrap: 'wrap', background: '#161b22', border: '1px solid #30363d', borderRadius: '8px', padding: '14px 20px' },
  summaryItem: { display: 'flex', alignItems: 'center', gap: '12px' },
  summaryLabel: { fontSize: '13px', fontWeight: '600', color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.05em' },
  summaryValue: { fontSize: '20px', fontWeight: '700', color: '#e6edf3', letterSpacing: '0.01em' },

  cardsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' },
  card: { background: '#161b22', border: '1px solid #30363d', borderRadius: '8px', padding: '18px', cursor: 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px', gap: '8px' },
  cardTitle: { fontWeight: '700', fontSize: '15px', color: '#e6edf3', lineHeight: '1.3', flex: 1, minWidth: 0, wordBreak: 'break-word' },
  cardFactory: { color: '#8b949e', fontSize: '13px', marginBottom: '14px', display: 'flex', alignItems: 'center' },
  cardFooter: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px' },
  cardDate: { color: '#8b949e', fontSize: '12px', whiteSpace: 'nowrap' },
  cardValue: { marginTop: '10px', textAlign: 'right', fontWeight: '700', fontSize: '15px', color: '#e6edf3' },
};

/* ── Style drawera (ds) ─────────────────────────────────────────────────── */

const ds = {
  /* Overlay */
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', zIndex: 999 },

  /* Panel */
  drawer: {
    position: 'fixed', top: 0, right: 0, height: '100vh', width: '60%',
    background: '#161b22', borderLeft: '1px solid #30363d',
    boxShadow: '-4px 0 32px rgba(0,0,0,0.6)', zIndex: 1000,
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
  },

  /* Nagłówek drawera */
  drawerHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '18px 24px', borderBottom: '1px solid #30363d',
    background: '#0d1117', flexShrink: 0, gap: '12px',
  },
  drawerTitle: { fontSize: '18px', fontWeight: '700', color: '#e6edf3', lineHeight: '1.2', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  drawerSubtitle: { fontSize: '13px', color: '#8b949e', marginTop: '4px' },
  drawerHeaderBtns: { display: 'flex', gap: '8px', flexShrink: 0 },
  btnEdit:   { padding: '7px 16px', background: '#1c2b3a', color: '#58a6ff', border: '1px solid #1f4068', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' },
  btnClone:  { padding: '7px 14px', background: '#21262d', color: '#c9d1d9', border: '1px solid #30363d', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' },
  btnReport: { padding: '7px 16px', background: '#21262d', color: '#c9d1d9', border: '1px solid #30363d', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' },
  btnClose:  { padding: '7px 13px', background: 'none', border: '1px solid #30363d', color: '#8b949e', borderRadius: '6px', fontSize: '16px', cursor: 'pointer', lineHeight: 1 },

  /* Treść (scrollowalna) */
  drawerBody: { flex: 1, overflowY: 'auto' },

  /* Sekcje */
  section: { padding: '20px 24px', borderBottom: '1px solid #21262d' },
  sectionTitle: { margin: '0 0 14px', fontSize: '11px', fontWeight: '700', color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.07em' },
  sectionRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '14px' },

  /* Dane podstawowe */
  infoGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 32px' },
  infoRow: { display: 'flex', flexDirection: 'column', gap: '3px' },
  infoLabel: { fontSize: '11px', color: '#8b949e', textTransform: 'uppercase', letterSpacing: '0.04em' },
  infoValue: { fontSize: '14px', color: '#c9d1d9' },

  /* Tabela pozycji */
  tableWrap: { overflowX: 'auto', borderRadius: '6px', border: '1px solid #30363d' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: '13px' },
  th:  { padding: '9px 12px', background: '#0d1117', textAlign: 'left',  borderBottom: '1px solid #30363d', fontWeight: '600', color: '#8b949e', whiteSpace: 'nowrap' },
  thR: { padding: '9px 12px', background: '#0d1117', textAlign: 'right', borderBottom: '1px solid #30363d', fontWeight: '600', color: '#8b949e', whiteSpace: 'nowrap' },
  td:  { padding: '9px 12px', borderBottom: '1px solid #21262d', color: '#c9d1d9', verticalAlign: 'middle' },
  tdR: { padding: '9px 12px', borderBottom: '1px solid #21262d', color: '#c9d1d9', textAlign: 'right', verticalAlign: 'middle' },
  trEven: { background: '#161b22' },
  trOdd:  { background: '#0d1117' },
  tfTd: { padding: '9px 12px', color: '#8b949e', fontSize: '13px', fontWeight: '600', borderTop: '1px solid #30363d' },
  badgeOk:        { background: '#1a2e1a', color: '#52c41a', border: '1px solid #2d5a2d', borderRadius: '4px', padding: '1px 7px', fontSize: '11px', fontWeight: '600' },
  badgeCancelled: { background: '#2d1b1b', color: '#ff7875', border: '1px solid #5c2323', borderRadius: '4px', padding: '1px 7px', fontSize: '11px', fontWeight: '600' },
  btnSmallBlue: { padding: '5px 12px', background: '#1c2b3a', color: '#58a6ff', border: '1px solid #1f4068', borderRadius: '5px', fontSize: '12px', cursor: 'pointer' },
  btnIcon:    { padding: '3px 7px', background: '#1c2b3a', color: '#58a6ff', border: '1px solid #1f4068', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' },
  btnIconDel: { padding: '3px 7px', background: '#2d1b1b', color: '#ff7875', border: '1px solid #5c2323', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' },
  btnTinyDel: { padding: '3px 9px', background: '#2d1b1b', color: '#ff7875', border: '1px solid #5c2323', borderRadius: '4px', fontSize: '12px', cursor: 'pointer' },

  /* Notatki */
  notesList: { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' },
  noteItem:  { background: '#0d1117', borderRadius: '6px', padding: '12px', border: '1px solid #21262d' },
  noteMeta:  { display: 'flex', gap: '6px', alignItems: 'center', marginBottom: '6px' },
  noteAuthor: { fontSize: '12px', fontWeight: '600', color: '#58a6ff' },
  dot:      { color: '#484f58', fontSize: '12px' },
  metaDate: { fontSize: '12px', color: '#484f58' },
  noteText: { margin: 0, fontSize: '14px', color: '#c9d1d9', lineHeight: '1.5' },

  /* Zdjęcia / załączniki */
  photoGrid:        { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '8px' },
  photoThumb:       { aspectRatio: '1', borderRadius: '6px', overflow: 'hidden', background: '#21262d', cursor: 'pointer', border: '1px solid #30363d' },
  photoImg:         { maxWidth: '120px', height: 'auto', display: 'block', borderRadius: '6px', border: '1px solid #30363d', cursor: 'pointer' },
  attachThumb:      { display: 'inline-block', marginBottom: '4px' },
  pdfLink:          { display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 12px', background: '#161b22', border: '1px solid #30363d', borderRadius: '6px', color: '#58a6ff', fontSize: '13px', textDecoration: 'none', marginBottom: '4px' },
  attachPreviewWrap: { position: 'relative', display: 'inline-block', marginBottom: '4px' },
  attachPreviewImg:  { maxWidth: '200px', maxHeight: '150px', borderRadius: '6px', border: '1px solid #30363d', display: 'block' },
  attachFilename:    { display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#8b949e', padding: '6px 10px', background: '#21262d', borderRadius: '6px' },
  btnClearAttach:    { background: 'none', border: 'none', color: '#8b949e', cursor: 'pointer', fontSize: '14px', padding: '2px 4px', lineHeight: '1' },
  btnAttach:         { padding: '7px 14px', background: '#21262d', color: '#c9d1d9', border: '1px solid #30363d', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' },

  /* Komentarze */
  commentsList: { display: 'flex', flexDirection: 'column', gap: '10px', marginBottom: '14px' },
  commentItem:  { background: '#0d1117', borderRadius: '6px', padding: '12px', border: '1px solid #21262d' },
  commentMeta:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
  commentText:  { margin: 0, fontSize: '14px', color: '#c9d1d9', lineHeight: '1.5' },
  commentThumb: { marginTop: '8px', maxHeight: '100px', borderRadius: '4px', cursor: 'pointer', display: 'block', border: '1px solid #30363d' },

  /* Formularz dodawania */
  addForm:  { display: 'flex', flexDirection: 'column', gap: '8px' },
  textarea: { width: '100%', padding: '9px 12px', background: '#0d1117', border: '1px solid #30363d', borderRadius: '6px', color: '#c9d1d9', fontSize: '14px', resize: 'vertical', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' },
  btnPrimary: { alignSelf: 'flex-end', padding: '8px 18px', background: '#1890ff', color: 'white', border: 'none', borderRadius: '6px', fontSize: '13px', cursor: 'pointer', fontWeight: '500' },

  /* Misc */
  errorBanner: { margin: '24px', background: '#2d1b1b', border: '1px solid #5c2323', color: '#ff7875', padding: '12px 16px', borderRadius: '6px' },
  spinner:     { width: '32px', height: '32px', margin: '0 auto', border: '3px solid #30363d', borderTopColor: '#1890ff', borderRadius: '50%', animation: 'ds-spin 0.8s linear infinite' },
  emptyText:   { color: '#484f58', fontSize: '14px', margin: '0 0 14px', fontStyle: 'italic' },

  /* Modal pozycji */
  modalOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1500, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { background: '#161b22', borderRadius: '10px', width: '100%', maxWidth: '560px', boxShadow: '0 8px 32px rgba(0,0,0,0.5)', border: '1px solid #30363d', margin: '16px' },
  modalHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 24px', borderBottom: '1px solid #30363d' },
  modalTitle: { margin: 0, fontSize: '16px', fontWeight: '700', color: '#e6edf3' },
  modalFooter: { display: 'flex', justifyContent: 'flex-end', gap: '10px', padding: '16px 24px', borderTop: '1px solid #30363d' },
  mField: { padding: '12px 24px 0' },
  mRow:   { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' },
  mLabel: { display: 'block', marginBottom: '6px', fontSize: '13px', fontWeight: '500', color: '#c9d1d9' },
  mInput: { width: '100%', padding: '9px 12px', border: '1px solid #30363d', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box', background: '#0d1117', color: '#c9d1d9', outline: 'none', fontFamily: 'inherit', colorScheme: 'dark' },
  mError: { color: '#ff7875', fontSize: '13px', margin: '10px 24px 0' },
  btnCancel:   { padding: '9px 18px', background: '#21262d', color: '#c9d1d9', border: '1px solid #30363d', borderRadius: '6px', fontSize: '14px', cursor: 'pointer' },
  btnSaveNext: { padding: '9px 18px', background: '#1c2b3a', color: '#58a6ff', border: '1px solid #1f4068', borderRadius: '6px', fontSize: '14px', cursor: 'pointer', fontWeight: '500' },
  btnSave:     { padding: '9px 18px', background: '#1890ff', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer', fontWeight: '500' },
  btnDelete: { padding: '9px 18px', background: '#ff4d4f', color: 'white', border: 'none', borderRadius: '6px', fontSize: '14px', cursor: 'pointer' },
  confirmText: { padding: '4px 24px 16px', color: '#c9d1d9', fontSize: '14px', lineHeight: '1.5', margin: 0 },

  /* Lightbox */
  lightboxOverlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.88)', zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  lightboxBox:     { position: 'relative', maxWidth: '90vw', maxHeight: '90vh' },
  lightboxImg:     { maxWidth: '100%', maxHeight: '90vh', borderRadius: '8px', display: 'block' },
  lightboxClose:   { position: 'absolute', top: '-14px', right: '-14px', width: '32px', height: '32px', background: '#21262d', border: '1px solid #30363d', color: '#e6edf3', borderRadius: '50%', cursor: 'pointer', fontSize: '14px', lineHeight: '32px', textAlign: 'center' },
};
